import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { QUESTIONS } from "@/data/audit";
import { api, formatError } from "@/lib/api";
import { Field, Stamp } from "@/components/Doc";
import { Report } from "@/components/Report";

const pad = (n) => String(n).padStart(2, "0");
const letters = ["a", "b", "c", "d"];

const Question = ({ q, index, onAnswer, onBack }) => (
  <div key={q.id} className="settle max-w-2xl mx-auto" data-testid={`question-${q.id}`}>
    <div className="flex justify-between items-end border-b border-rule pb-2">
      <span className="t-label text-olive" data-testid="progress-indicator">Question {pad(index + 1)} of {pad(QUESTIONS.length)}</span>
      <span className="t-label text-rule">Form LH-90</span>
    </div>
    <h2 className="t-head text-3xl sm:text-4xl lg:text-5xl mt-10 leading-[1]" data-testid="question-text">{q.text}</h2>
    <div className="mt-10 flex flex-col gap-3">
      {q.answers.map((a, i) => (
        <button key={i} className="answer-card" onClick={() => onAnswer(i)} data-testid={`answer-${i}`}>
          <span className="t-label text-olive mr-4">({letters[i]})</span>
          <span className="t-body text-lg sm:text-xl">{a}</span>
        </button>
      ))}
    </div>
    <div className="mt-8 flex justify-between">
      {index > 0 ? <button onClick={onBack} className="t-label text-olive hover:text-carbon" data-testid="back-btn">← Previous</button> : <span />}
      <span className="t-label text-rule">Answers are unscored until submission</span>
    </div>
  </div>
);

const Gate = ({ onSubmit, busy, team }) => {
  const [f, setF] = useState({ name: "", email: "", company: team?.company || "", role: "" });
  const set = (e) => setF({ ...f, [e.target.name]: e.target.value });
  return (
    <form className="settle max-w-xl mx-auto bg-paper border border-carbon p-7 sm:p-10 shadow-[6px_6px_0_0_#D9C9A8] relative" onSubmit={(e) => { e.preventDefault(); onSubmit(f); }} data-testid="email-gate-form">
      <div className="absolute -top-4 right-6"><Stamp color="carbon" rotate={5} size="sm">Form LH-90</Stamp></div>
      <span className="t-label text-olive">Section 11 — Requestor</span>
      <h2 className="t-head text-3xl sm:text-4xl mt-3">Submit for preliminary findings</h2>
      <div className="mt-8 flex flex-col gap-6">
        <Field label="Name" name="name" value={f.name} onChange={set} required testId="gate-name" />
        <Field label="Work email" name="email" type="email" value={f.email} onChange={set} required testId="gate-email" />
        <Field label="Company" name="company" value={f.company} onChange={set} required testId="gate-company" />
        <Field label="Role" name="role" value={f.role} onChange={set} required testId="gate-role" />
      </div>
      <button type="submit" className="btn btn-stamp mt-10 w-full sm:w-auto" disabled={busy} data-testid="gate-submit-btn">
        {busy ? "Preparing findings…" : "Submit for Preliminary Findings"}
      </button>
      <p className="t-body italic text-base text-olive mt-5">We'll send the report and nothing else. No sequence, no newsletter.</p>
      {team && <p className="t-body text-base text-olive mt-2" data-testid="gate-team-note">Filed to the team audit for {team.company}. {team.sponsor_name} will see your answers by name.</p>}
    </form>
  );
};

export default function Audit() {
  const [params] = useSearchParams();
  const teamCode = params.get("team");
  const [team, setTeam] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  useEffect(() => {
    if (!teamCode) return;
    api.get(`/team/${teamCode}`).then((r) => setTeam(r.data)).catch(() => toast.error("Team audit code not recognised"));
  }, [teamCode]);

  const answer = (i) => { setAnswers([...answers.slice(0, step), i]); setStep(step + 1); };

  const submit = async (f) => {
    setBusy(true);
    try {
      const { data } = await api.post("/audit/submit", { ...f, answers, team_code: team ? team.code : null });
      setReport(data);
      window.scrollTo(0, 0);
      toast(data.email_sent ? "Findings filed and sent to your inbox" : "Findings filed. Email could not be sent.");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-[calc(100vh-4rem)] px-5 sm:px-8 py-16 sm:py-24" data-testid="audit-page">
      {team && !report && (
        <div className="no-print max-w-2xl mx-auto mb-10 bg-manila border border-rule px-5 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2" data-testid="team-banner">
          <span className="t-label text-carbon">Team audit — {team.company} · convened by {team.sponsor_name}</span>
          <span className="t-label text-olive">Code {team.code} · answers shared with sponsor</span>
        </div>
      )}
      {report ? (
        <div className="settle">
          <div className="no-print max-w-3xl mx-auto flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-8">
            <span className="t-label text-olive">Findings rendered. {report.email_sent ? "A copy has been sent to your email." : ""}</span>
            <div className="flex gap-3">
              {!report.team_code && <Link to="/team" className="btn" data-testid="run-team-audit-btn">Run It Across Your Team</Link>}
              <button onClick={() => window.print()} className="btn" data-testid="download-pdf-btn">Download as PDF</button>
            </div>
          </div>
          <Report report={report} />
        </div>
      ) : step < QUESTIONS.length ? (
        <Question q={QUESTIONS[step]} index={step} onAnswer={answer} onBack={() => setStep(step - 1)} />
      ) : (
        <Gate onSubmit={submit} busy={busy} team={team} />
      )}
    </main>
  );
}
